import { useCallback, useMemo } from 'react';

import { Table } from '@tanstack/react-table';

import { useTheme } from '@adsk/alloy-react-styles';

import { SkeletonRow, Themeable } from '../types';

type UseSkeletonLoadingRowsArgs<D extends Record<string, unknown>> =
  Themeable & {
    table: Table<D>;
    numberOfRows?: number;
    columnIdsToRemove?: string[];
  };

export default <D extends Record<string, unknown>>({
  table,
  tableSize,
  numberOfRows = 8,
  columnIdsToRemove = ['actions'],
}: UseSkeletonLoadingRowsArgs<D>) => {
  const theme = useTheme();
  const shimmer = useMemo(
    () => theme.tokens?.component?.table?.skeleton?.shimmer ?? [],
    [theme],
  );

  const getSkeletonLoadingRows = useCallback(
    () =>
      Array.from({ length: numberOfRows }).map((_, index) => ({
        id: `skeleton-row-${index}`,
        index,
      })),
    [numberOfRows],
  );

  const tableCells = useMemo(
    () =>
      table
        .getFlatHeaders()
        .map((r) => r.column)
        .filter((c) => !columnIdsToRemove.includes(c.id))
        .map((column) => ({
          columnId: column.id,
          tableSize,
          size: column.getSize(),
          isResizing: column.getIsResizing(),
          column,
        })),
    [table, columnIdsToRemove, tableSize],
  );

  const getSkeletonLoadingRowProps = useCallback(
    (row: SkeletonRow) => ({
      ...row,
      ...(shimmer.length > 0
        ? shimmer[Math.min(row.index, shimmer.length - 1)]
        : {}),
      tableSize,
      tableCells,
    }),
    [tableCells, tableSize, shimmer],
  );

  return { getSkeletonLoadingRows, getSkeletonLoadingRowProps };
};
